import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Check, Phone as PhoneIcon, Mail as MailIcon, UserPlus, ArrowLeft, Info } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import PhoneOtpLogin from "@/components/auth/PhoneOtpLogin";
import EmailOtpVerify from "@/components/auth/EmailOtpVerify";
import { Checkbox } from "@/components/ui/checkbox";
import { TERMS_VERSION } from "@/pages/Terms";
import { PRIVACY_VERSION } from "@/pages/PrivacyPolicy";
import { useNoIndex } from "@/hooks/use-no-index";

/**
 * Owner registration — multi-step wizard.
 *
 * When `REACT_APP_PHONE_AUTH_ENABLED === "true"` (Firebase fully configured):
 *   Step 1 (Phone) → Step 2 (Email) → Step 3 (Details)
 *
 * When phone-auth is disabled (e.g. Firebase API key suspended, or product
 * not yet provisioned):
 *   Step 1 (Email) → Step 2 (Details)
 *
 * The phone-required path can be toggled back on by flipping the env flag
 * once the Firebase issue is resolved — no code changes needed.
 */
// Treat anything other than the explicit string "false"/"0"/"off" as enabled,
// so leaving the env var unset defaults to the safer (more-verification) flow.
const PHONE_AUTH_ENABLED = (() => {
  const v = process.env.REACT_APP_PHONE_AUTH_ENABLED;
  if (v == null || v === "") return true;
  return !["false", "0", "off", "no"].includes(String(v).toLowerCase());
})();

const ALL_STEPS = [
  { id: "phone",   label: "Phone",   icon: PhoneIcon },
  { id: "email",   label: "Email",   icon: MailIcon },
  { id: "details", label: "Details", icon: UserPlus },
];
const STEPS = PHONE_AUTH_ENABLED ? ALL_STEPS : ALL_STEPS.filter((s) => s.id !== "phone");

export default function Register() {
  const { setUser } = useAuth();
  const nav = useNavigate();

  // First step depends on whether phone auth is enabled.
  const [stepId, setStepId] = useState(PHONE_AUTH_ENABLED ? "phone" : "email");
  const [phone, setPhone] = useState(null);
  const [phoneToken, setPhoneToken] = useState(null);
  const [email, setEmail] = useState("");
  const [emailToken, setEmailToken] = useState(null);

  // Step-final form
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { document.title = "Register as Spa Owner - Pune Spa Guide"; }, []);
  useNoIndex();

  const stepIndex = useMemo(() => STEPS.findIndex((s) => s.id === stepId), [stepId]);

  /** Move to the next step after a successful verification. */
  const advance = (to) => setStepId(to);

  /** Phone verification handler — only used when PHONE_AUTH_ENABLED. */
  const onPhoneVerified = ({ phone: verifiedPhone, verification_token }) => {
    setPhone(verifiedPhone);
    setPhoneToken(verification_token);
    advance("email");
  };

  /** Email verification handler. */
  const onEmailVerified = ({ email: verifiedEmail, verification_token }) => {
    setEmail(verifiedEmail);
    setEmailToken(verification_token);
    advance("details");
  };

  /** Final step — create the account. */
  const createAccount = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please enter your full name.");
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (password !== confirmPassword) return toast.error("Passwords do not match.");
    if (!termsAccepted) return toast.error("Please accept the Privacy Policy and Terms & Conditions to continue.");
    if (!emailToken) {
      toast.error("Please complete email verification first.");
      setStepId("email");
      return;
    }
    if (PHONE_AUTH_ENABLED && !phoneToken) {
      toast.error("Please complete phone verification first.");
      setStepId("phone");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        email,
        password,
        role: "owner",
        email_verification_token: emailToken,
        // Single UI checkbox → two policy acceptances on the backend so each
        // policy can be re-accepted independently if we bump one but not the other.
        terms_accepted: true,
        terms_version: TERMS_VERSION,
        privacy_accepted: true,
        privacy_version: PRIVACY_VERSION,
      };
      if (phoneToken) payload.phone_verification_token = phoneToken;
      const res = await api.post("/auth/register", payload);
      localStorage.setItem("psg_user", JSON.stringify(res.data.user));
      setUser(res.data.user);
      toast.success(`Welcome, ${res.data.user.name}!`);
      nav("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not create your account. Please try again.";
      if (/email verification expired/i.test(detail)) setStepId("email");
      else if (/phone verification expired/i.test(detail) && PHONE_AUTH_ENABLED) setStepId("phone");
      toast.error(detail, { duration: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="register-owner-page" className="max-w-md mx-auto px-4 py-12">
      <div className="rounded-2xl bg-white border border-[#E7DDCF] p-8 shadow-[0_12px_40px_rgba(17,24,39,0.06)]">
        <div className="text-xs uppercase tracking-widest text-[#7A5A22]">List Your Spa</div>
        <h1 className="font-heading text-3xl mt-2">Create Owner Account</h1>
        <p className="text-sm text-[#4B5563] mt-1">
          {PHONE_AUTH_ENABLED
            ? "Verify your phone and email to keep your listing trusted and abuse-free."
            : "Verify your email to keep your listing trusted and abuse-free."}
        </p>

        {!PHONE_AUTH_ENABLED && (
          <div
            className="mt-4 rounded-md border border-dashed border-[#E7DDCF] bg-[#FBF7F1] px-3 py-2 text-[11px] text-[#7A5A22] flex items-start gap-2"
            data-testid="phone-auth-disabled-banner"
          >
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>
              <strong>Phone verification is temporarily unavailable.</strong>{" "}
              You can complete your registration with email verification only — you&apos;ll be able to add and verify your phone number later from the dashboard.
            </span>
          </div>
        )}

        {/* Stepper */}
        <ol className="mt-6 flex items-center justify-between gap-2" data-testid="register-stepper">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < stepIndex;
            const active = i === stepIndex;
            return (
              <li key={s.id} className="flex-1 flex items-center gap-2" data-testid={`step-${s.id}`}>
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full border text-xs font-semibold ${
                    done
                      ? "bg-[#1F4E3D] border-[#1F4E3D] text-white"
                      : active
                        ? "bg-[#C9A86A] border-[#C9A86A] text-[#111827]"
                        : "bg-white border-[#E7DDCF] text-[#7A5A22]"
                  }`}
                  aria-current={active ? "step" : undefined}
                >
                  {done ? <Check size={14} /> : <Icon size={14} />}
                </div>
                <div className={`text-[11px] uppercase tracking-wider ${active ? "text-[#111827] font-semibold" : "text-[#4B5563]"}`}>
                  {s.label}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-px ${done ? "bg-[#1F4E3D]" : "bg-[#E7DDCF]"}`} />
                )}
              </li>
            );
          })}
        </ol>

        <div className="mt-6">
          {stepId === "phone" && PHONE_AUTH_ENABLED && (
            <div data-testid="register-step-phone">
              <PhoneOtpLogin
                mode="register"
                hideNameField
                onVerified={onPhoneVerified}
              />
            </div>
          )}

          {stepId === "email" && (
            <div data-testid="register-step-email" className="space-y-3">
              {phone && (
                <div className="rounded-md border border-[#E7DDCF] bg-[#F5EFE6] px-3 py-2 text-xs text-[#4B5563] flex items-center gap-2" data-testid="phone-verified-badge">
                  <Check size={14} className="text-[#1F4E3D]" />
                  <span><strong className="text-[#111827]">{phone}</strong> verified</span>
                </div>
              )}
              <EmailOtpVerify onVerified={onEmailVerified} defaultEmail={email} />
            </div>
          )}

          {stepId === "details" && (
            <form onSubmit={createAccount} data-testid="register-step-details" className="space-y-4">
              <div className="rounded-md border border-[#E7DDCF] bg-[#F5EFE6] px-3 py-2 text-xs text-[#4B5563] space-y-1">
                {phone && (
                  <div className="flex items-center gap-2"><Check size={14} className="text-[#1F4E3D]" /><strong className="text-[#111827]">{phone}</strong> verified</div>
                )}
                <div className="flex items-center gap-2"><Check size={14} className="text-[#1F4E3D]" /><strong className="text-[#111827]">{email}</strong> verified</div>
              </div>
              <div>
                <Label className="text-xs">Full Name</Label>
                <Input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  className="mt-1.5"
                  data-testid="register-name-input"
                />
              </div>
              <div>
                <Label className="text-xs">Password <span className="text-[#4B5563] font-normal">(min 6 characters)</span></Label>
                <Input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className="mt-1.5"
                  data-testid="register-password-input"
                />
              </div>
              <div>
                <Label className="text-xs">Confirm Password</Label>
                <Input
                  type="password"
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  className="mt-1.5"
                  data-testid="register-confirm-password-input"
                />
              </div>

              {/* Mandatory Privacy + Terms acceptance — single checkbox, both hyperlinked.
                  Server-side registration will reject the request if either flag is missing. */}
              <label
                className="flex items-start gap-3 cursor-pointer select-none pt-1"
                data-testid="register-terms-block"
              >
                <Checkbox
                  checked={termsAccepted}
                  onCheckedChange={(v) => setTermsAccepted(Boolean(v))}
                  className="mt-0.5 border-[#1F4E3D] data-[state=checked]:bg-[#1F4E3D] data-[state=checked]:border-[#1F4E3D]"
                  data-testid="register-terms-checkbox"
                  aria-describedby="register-terms-copy"
                />
                <span id="register-terms-copy" className="text-[13px] leading-relaxed text-[#1F2937]">
                  I have read and agree to the{" "}
                  <Link
                    to="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1F4E3D] font-medium underline underline-offset-2 hover:text-[#2E5D4E]"
                    data-testid="register-privacy-link"
                  >
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1F4E3D] font-medium underline underline-offset-2 hover:text-[#2E5D4E]"
                    data-testid="register-terms-link"
                  >
                    Terms &amp; Conditions
                  </Link>
                  .
                </span>
              </label>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setStepId("email")}
                  className="inline-flex items-center gap-1 text-xs text-[#7A5A22] hover:text-[#1F4E3D]"
                  data-testid="register-back-button"
                >
                  <ArrowLeft size={12}/> Back
                </button>
                <Button
                  type="submit"
                  disabled={submitting || !termsAccepted}
                  className="flex-1 h-11 bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827] disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="register-submit-button"
                >
                  {submitting ? "Creating account..." : "Register"}
                </Button>
              </div>
            </form>
          )}
        </div>

        <div className="mt-6 text-xs text-center text-[#4B5563]">
          Already registered?{" "}
          <Link to="/login" className="text-[#1F4E3D] font-medium hover:underline">Login</Link>
        </div>
      </div>
    </div>
  );
}
